import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Page } from "../../../components/Page";
import { TooltipLayer } from "./components/TooltipLayer";
import Spinner from "../../../components/Spinners";
import { BackBar } from "./components/BackBar";
import { Chip, EngineChips } from "./components/Chip";
import { StateChip, StateChipBare } from "./components/StateChip";
import { SectionCard, TileLabel } from "./components/SectionCard";
import { HealthRulesCard } from "./components/HealthRulesCard";
import { HotelMetricTiles } from "./components/HotelMetricTiles";
import { RouteChain } from "./components/RouteChain";
import { IncidentPanel } from "./components/IncidentPanel";
import { VerdictCounters } from "./components/VerdictCounters";
import { LockHistoryModal } from "./components/LockHistoryModal";
import { RemoveHistoryRowModal } from "./components/modals/RemoveHistoryRowModal";
import { LoadError } from "./components/LoadError";
import { WebsiteLink } from "./components/WebsiteLink";
import { TrackingIds } from "./components/TrackingIds";
import { CredentialsStrip } from "./components/CredentialsStrip";
import { PipelineCard } from "./components/PipelineDiagram";
import { BrandAssetsDrawer } from "./components/BrandAssetsDrawer";
import { ModeBadge, PipelineStepSummary } from "./components/OnboardingSection";
import { UserDetailPanel } from "../../onboarding-tracking/components/UserDetailPanel";
import {
  ActionModal,
  FIELD_LABEL_CLASS,
  INPUT_CLASS,
} from "./components/modals/ActionModal";
import { AccessModeModal } from "./components/modals/AccessModeModal";
import { LockActionModal } from "./components/modals/LockActionModal";
import { OverrideModal } from "./components/modals/OverrideModal";
import { ProofRequirementModal } from "./components/modals/ProofRequirementModal";
import { AcceptConditionModal } from "./components/modals/AcceptConditionModal";
import { ConditionActionModal } from "./components/modals/ConditionActionModal";
import {
  useAddCondition,
  useApplyLock,
  useApplyOverride,
  useClearOverride,
  useConfirmCondition,
  useDiagnoseNow,
  useFleetMetrics,
  useIncident,
  useRemoveCondition,
  useRemoveHistoryRow,
  useRemoveLock,
  useSetAccessMode,
  useSetProofRequirement,
  useHotelDetail,
} from "./hooks/useTrackingHealth";
import { useStickyCollapsed } from "./hooks/useStickyCollapse";
import {
  normalizeCustomerId,
  useOnboardingPipelines,
} from "./hooks/useOnboardingPipelines";
import {
  evidenceSentence,
  fmtActor,
  fmtDate,
  fmtMoney,
  toHotelWebsite,
} from "./format";
import {
  CONDITION_CONFIRM_DISCLOSURE,
  CONDITION_REMOVE_DISCLOSURE,
  CONDITION_REVIEW_FLAG_EXPLANATION,
  ENGINE_UNPROVEN_HINT,
  ENGINE_UNPROVEN_TAG,
  LOCK_EXPLANATIONS,
  LOCK_SHORT_HINTS,
  LOW_VOLUME_EXIT_DISCLOSURE,
  PROBLEM_SHORT_LABELS,
  TDSD_PROVENANCE_DISPLAY,
  UI_ONLY_ACCESS_EXPLANATION,
  EXCLUDED_BANNER_TITLE,
  EXCLUDED_FROZEN_NOTE,
  PAUSED_MOVED_TO_CRM_LINK,
  UI_ONLY_ACCESS_INCIDENT_DISCLOSURE,
  UI_ONLY_ACCESS_TAG,
  uiOnlyAccessHint,
} from "./vocabulary";
import type {
  ConditionRow,
  HistoryLog,
  HotelDetail as HotelDetailData,
  IncidentEventRow,
  LockFacts,
  LowVolumeFacts,
  OverrideState,
  ProblemId,
  RuleRow,
  TrackingLockKind,
} from "./types";
import type { ProvisioningUser } from "../../onboarding-tracking/types";

// No `log-fix` here since 2026-08-13: a fix is logged against a PROBLEM, so
// the modal is opened from that problem's own fix column inside IncidentPanel.
type ModalState =
  | { kind: "lock"; lock: TrackingLockKind; action: "apply" | "remove" }
  | { kind: "override" }
  | { kind: "clear-override" }
  | { kind: "proof" }
  | { kind: "access" }
  | { kind: "lock-history" }
  // The three ends of an accepted condition's life. Accepting is rule-scoped
  // here — the problem-scoped half lives on the incident's problem cards,
  // where the finding it disposes of is.
  | { kind: "accept-condition"; rule: RuleRow }
  | { kind: "condition"; condition: ConditionRow; action: "remove" | "confirm" }
  | null;

const ADMIN_BUTTON_CLASS =
  "px-3 py-2 text-sm font-medium rounded-md border border-[#E9E9EB] bg-white text-content-secondary hover:bg-gray-50 hover:text-content-primary";

export function HotelDetail() {
  const { customerId = "" } = useParams<{ customerId: string }>();
  const detail = useHotelDetail(customerId);
  const pipelines = useOnboardingPipelines();
  // The census metric columns — the same read the fleet table joins, so the
  // numbers an operator clicked through on are the numbers they land on.
  const metrics = useFleetMetrics();
  // The open incident's thread, for the fix events the timeline card shows.
  // Same query key as the incident panel's, so this costs no extra request.
  const incidentQuery = useIncident(detail.data?.openIncident?.id ?? "");
  const [modal, setModal] = useState<ModalState>(null);
  const [onboardingDrawer, setOnboardingDrawer] = useState<
    "pipeline" | "brand-assets" | null
  >(null);

  const applyLock = useApplyLock(customerId);
  const removeLock = useRemoveLock(customerId);
  const applyOverride = useApplyOverride(customerId);
  const clearOverride = useClearOverride(customerId);
  const setProof = useSetProofRequirement(customerId);
  const setAccessMode = useSetAccessMode(customerId);
  const addCondition = useAddCondition(customerId);
  const removeCondition = useRemoveCondition(customerId);
  const confirmCondition = useConfirmCondition(customerId);
  // History-row removal is page-level rather than per-card: the same act
  // reaches the timeline, the fixes list and the lock-history modal, and one
  // confirmation dialog means the copy explaining what removal does NOT do is
  // written once.
  const [removingRow, setRemovingRow] = useState<RemovableRow | null>(null);
  const removeHistoryRow = useRemoveHistoryRow(
    customerId,
    detail.data?.openIncident?.id,
  );

  // The page-owned diagnose flow: ONE run drives the triage button AND the
  // developer pipeline's animation. The endpoint returns the run OUTCOME;
  // the pipeline card fetches the record by the returned id.
  const diagnose = useDiagnoseNow(customerId);
  const [freshRunId, setFreshRunId] = useState<string | null>(null);
  const [animSignal, setAnimSignal] = useState(0);
  // Sticky: a developer who works with the pipeline open wants it open on the
  // next hotel too. Stored collapsed-side-up so the default stays "closed".
  const [pipelineCollapsed, togglePipeline, setPipelineCollapsed] =
    useStickyCollapsed("hotel-dev-diagnostics", true);
  const pipelineOpen = !pipelineCollapsed;
  const setPipelineOpen = (open: boolean) => setPipelineCollapsed(!open);

  const runDiagnose = () => {
    // Watching the run IS the point — open the pipeline and bring it into
    // view; the stages pulse while the run executes.
    setPipelineOpen(true);
    window.setTimeout(() => {
      document
        .getElementById("th-pipeline")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
    diagnose.mutate(undefined, {
      onSuccess: (outcome) => {
        if (outcome.runId) {
          setFreshRunId(outcome.runId);
          setAnimSignal((n) => n + 1);
        }
      },
    });
  };

  if (detail.isLoading) {
    return (
      <Shell>
        <div className="flex justify-center py-16">
          <Spinner className="!text-brand-primary" />
        </div>
      </Shell>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <Shell>
        <LoadError
          error={detail.error}
          label="this hotel"
          onRetry={() => detail.refetch()}
        />
      </Shell>
    );
  }

  const hotel = detail.data;
  const website = toHotelWebsite(hotel.websiteUrl);
  const goLiveEvidence = evidenceSentence(hotel.goLive.evidence);
  const hotelMetrics = metrics.data?.rows.find(
    (row) => row.customerId === hotel.customerId,
  );
  // The open incident's thread feeds two halves of the history card: the
  // fixes tried, and the human dispositions (a silence, and its ending) that
  // change nothing about the hotel's state but everything about what an
  // operator should expect from it.
  const incidentEvents = incidentQuery.data?.events ?? [];
  const fixEvents = incidentEvents
    .filter((event) => event.type === "fix-logged")
    .slice(0, 6);

  // The last validator run is a developer fact — it labels the diagnostics
  // block rather than the header, where it competed with the two facts that
  // actually decide verification.
  const lastRunLabel = hotel.lastRun
    ? `last run ${fmtDate(hotel.lastRun.startedAt)} (${
        hotel.lastRun.trigger === "go-live" &&
        hotel.lastRun.actor === "system:go-live-backstop"
          ? "go-live backstop"
          : `${hotel.lastRun.trigger} trigger`
      })`
    : hotel.awaitingGoLiveRun
      ? "no run yet · queued (go-live backstop)"
      : "no run yet";

  /** The condition relieving a rule — by the record's own id first, the
   *  active rule-scoped condition as fallback. */
  const conditionForRule = (
    rule: HotelDetailData["rules"][number],
  ): ConditionRow | undefined =>
    hotel.conditions.find((c) => c.id === rule.acceptedConditionId) ??
    hotel.conditions.find(
      (c) =>
        c.status === "active" &&
        c.scopeType === "rule" &&
        c.scopeId === rule.rule,
    );

  // The hotel's SEM-provisioning pipeline, when one exists — joined by the
  // platform user first, the Ads customer id as fallback.
  const pipelineRows = pipelines.data ?? [];
  const pipelineUser =
    (hotel.userId
      ? pipelineRows.find((u) => u.userId === hotel.userId)
      : undefined) ??
    pipelineRows.find(
      (u) =>
        normalizeCustomerId(u.googleAdsCustomerId) ===
        normalizeCustomerId(hotel.customerId),
    ) ??
    null;
  // Mid-onboarding hotels get the onboarding card up top — the health cards
  // below are gate copy until the campaigns serve.
  const stillOnboarding = hotel.status.gate === "not-yet-live";
  // ADR 0041. The page stays fully readable — it is the one surface that can
  // say WHY this hotel is quiet — but nothing below is a live reading, and
  // every write is refused by the server, so the controls disable from this
  // rather than being allowed to 409.
  const excluded = hotel.exclusion;

  return (
    <Shell>
      {excluded && (
        <div className="mb-3 rounded-md border border-[#E9E9EB] bg-surface-muted px-4 py-3 space-y-1">
          <p className="text-sm font-medium text-content-primary">
            {EXCLUDED_BANNER_TITLE}
            {excluded.since ? ` · since ${fmtDate(excluded.since)}` : ""}
          </p>
          <p className="text-xs text-content-secondary">{excluded.sentence}</p>
          <p className="text-xs text-gray-400">
            {EXCLUDED_FROZEN_NOTE}
            {excluded.frozenOpenIncidentId
              ? " The incident below is still open and will be picked back up where it left off."
              : ""}
          </p>
          {excluded.liftableInCrm && (
            <Link
              to="/crm/customers"
              className="inline-block text-xs font-medium text-brand-primary hover:underline"
            >
              {PAUSED_MOVED_TO_CRM_LINK} →
            </Link>
          )}
        </div>
      )}
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
        {/* Capped so the credentials strip can actually sit beside it; the
            chips and meta facts wrap rather than claim the row. */}
        <div className="min-w-0 max-w-md">
          <BackBar
            crumbs={[
              { label: "Tracking Health", to: "/tracking/health" },
              { label: hotel.hotelName },
            ]}
          />
          <div className="flex items-center gap-2 flex-wrap mt-1">
            <h1 className="text-2xl font-semibold text-black">
              {hotel.hotelName}
            </h1>
            <StateChip status={hotel.status} />
            <EngineChips engines={hotel.engines} />
            {hotel.engineUnproven && (
              <Chip tone="purple" title={ENGINE_UNPROVEN_HINT}>
                {ENGINE_UNPROVEN_TAG}
              </Chip>
            )}
            <Chip tone="neutral">
              {hotel.trackingMode === "gtm"
                ? "GTM-tracked"
                : hotel.trackingMode === "ga4-only"
                  ? "GA4-only"
                  : "no GA4 / no GTM"}
            </Chip>
            {/* Beside the tracking-mode chip deliberately: the pair reads as
                "what measures this hotel, and what we can do about it". */}
            {/* Neutral — the palette's "facts & gates", which is exactly what
                this is. It was orange ("waiting / caution") until 2026-08-15;
                see the Access strip below for why that reading was wrong. */}
            {hotel.access.mode === "ui-only" && (
              <Chip tone="neutral" title={uiOnlyAccessHint(hotel.access.note)}>
                {UI_ONLY_ACCESS_TAG}
              </Chip>
            )}
          </div>
          {/* Three meta lines — who the hotel is, what it spends, then the
              ids it is measured by — split so the block stays inside its
              width cap. The Ads customer id and the canonical timezone were
              dropped 2026-08-13: an admin does not recognise a hotel by its
              10-digit account number, and the timezone only ever mattered to
              the ingest. The trusted data start date is here because it IS a
              standing fact about the hotel; the proof count left because it
              is verification state, and reads on the incident that is
              waiting for it. The site sits beside the owner's address
              because the pair is the hotel's identity — and because half of
              diagnosing this hotel starts with opening it. */}
          <div className="text-sm text-content-secondary mt-1">
            {hotel.ownerEmail ?? "no platform user matched"}
            {website && (
              <>
                {" · "}
                <WebsiteLink website={website} />
              </>
            )}
          </div>
          <div className="text-sm text-content-secondary">
            spend 30d {fmtMoney(hotel.spend30d, hotel.currency)}
            {" · "}
            <span data-tip="Every window a rule judges starts here; logging a fix restarts it, so the booking that proves the fix must come after it">
              trusted data since{" "}
              <span className="font-medium text-content-primary">
                {fmtDate(hotel.tdsd.date)}
              </span>{" "}
              <span className="text-gray-500">
                ({TDSD_PROVENANCE_DISPLAY[hotel.tdsd.provenance]})
              </span>
            </span>
          </div>
          <TrackingIds
            gtmContainerPublicId={hotel.gtmContainerPublicId}
            ga4MeasurementId={hotel.ga4MeasurementId}
          />
        </div>
        {/* The header's middle column, which was empty. These are the hotel's
            logins: half of diagnosing it needs them, and none of it is
            identified by them — so they sit beside the identity block rather
            than inside it, and they are on screen from the first paint. */}
        <CredentialsStrip userId={hotel.userId} />
        {/* Only the two acts with no home of their own. Log fix lives on the
            incident it writes into; the locks are the R / V glyphs on the
            locks card, which is also where their state is read. */}
        <div className="flex flex-col items-end gap-2">
          <button
            onClick={() =>
              setModal(
                hotel.status.override
                  ? { kind: "clear-override" }
                  : { kind: "override" },
              )
            }
            disabled={!!excluded}
            data-tip={
              excluded
                ? excluded.sentence
                : hotel.status.override?.state === "low-volume"
                  ? "Remove this hotel from the Low volume category — since ADR 0040 it holds no locks, so this only lifts the label"
                  : "Force the REPORTED status among healthy / monitoring / unhealthy / low volume — the computation keeps running underneath and stays visible. Paused is set in the CRM now"
            }
            className={`${ADMIN_BUTTON_CLASS}${excluded ? " opacity-50 cursor-not-allowed hover:bg-white hover:text-content-secondary" : ""}`}
          >
            {hotel.status.override?.state === "low-volume"
              ? "Remove from Low volume"
              : `${hotel.status.override ? "Clear" : "Set"} status override`}
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setModal({ kind: "proof" })}
              data-tip="Raise (or reset) how many qualifying bookings this hotel needs to leave Monitoring"
              className={ADMIN_BUTTON_CLASS}
            >
              Proof requirement
            </button>
            <button
              onClick={() => setModal({ kind: "access" })}
              data-tip="Record whether Autumn can change this hotel's GTM container and GA4 property itself, or only through a seat in the customer's own console"
              className={ADMIN_BUTTON_CLASS}
            >
              Access
            </button>
          </div>
        </div>
      </div>

      {/* Context note strip — standing, hand-curated; color for a human,
          nothing computes from it. Never styled as a finding (§5.2).
          Provenance rides the hover, never the body: these notes describe
          things observed long before the pass that loaded them, so an
          on-screen date would read as news. Capped and scrollable — the
          longest seeded note runs about sixty words and must not push the
          verdicts below the fold. */}
      {hotel.contextNote && (
        <div
          className="bg-[#F4F4F6] rounded-lg px-4 py-3 mb-4 mt-2 max-h-[168px] overflow-y-auto"
          data-tip={contextProvenance(hotel)}
        >
          <TileLabel>Context</TileLabel>
          <p className="text-sm text-content-primary whitespace-pre-wrap">
            {hotel.contextNote}
          </p>
        </div>
      )}

      {/* The derived Low-volume reading (ADR 0039) — the daily pass's own
          classification of a HEALTHY hotel, in the context strip's neutral
          register: a fact about the hotel's volume, never a finding. It
          spells out what the allowance does and does not stand down, because
          the declared category beside it in every reader's memory locks
          everything and this locks nothing. Keyed on `lowVolume`, not on the
          override — the two can stand together, and the override button
          above keeps its own logic (it reads `override` alone). */}
      {hotel.status.lowVolume && (
        <LowVolumeStrip facts={hotel.status.lowVolume} />
      )}

      {/* UI-only access (backend issue 48). Standing fact, so it renders
          whenever it is true — but it changes register when an incident is
          open, because that is the moment it stops being trivia and starts
          deciding what the operator does next. Nothing downstream can say
          it: the fix plan labels steps `automatic` from the surfaces Autumn
          owns IN GENERAL, and the catalog has never heard of this hotel.

          **Neutral, not amber** (2026-08-15). It was amber, on the reasoning
          that an open incident makes this consequential. But amber in this
          dashboard is reserved for *waiting / caution*, and this is neither:
          it is a standing fact about what Autumn holds, and the operator's
          response to it is to work in a different console, not to worry. The
          same correction was made to the step tags, for the same reason.

          What carries the weight instead is placement and emphasis: the strip
          sits above the incident, and the consequence line goes bold and dark
          when one is open. */}
      {hotel.access.mode === "ui-only" && (
        <div
          className={`rounded-lg px-4 py-3 mb-4 mt-2 text-sm bg-[#F4F4F6] ${
            hotel.openIncident ? "border border-[#D8D8DC]" : ""
          }`}
        >
          <TileLabel>Access</TileLabel>
          <p className="text-content-primary">{UI_ONLY_ACCESS_EXPLANATION}</p>
          {hotel.access.note && (
            <p className="mt-1 text-content-primary whitespace-pre-wrap">
              {hotel.access.note}
            </p>
          )}
          {hotel.openIncident && (
            <p className="mt-1.5 font-medium text-content-primary">
              {UI_ONLY_ACCESS_INCIDENT_DISCLOSURE}
            </p>
          )}
          {hotel.access.declaredAt && (
            <p className="mt-1.5 text-xs text-gray-500">
              Recorded by {fmtActor(hotel.access.declaredBy) || "unknown"} on{" "}
              {fmtDate(hotel.access.declaredAt)}
            </p>
          )}
        </div>
      )}

      {/* Mid-onboarding: the pipeline is the story — it leads, full width. */}
      {stillOnboarding && (
        <div className="my-4">
          <OnboardingCard
            hotel={hotel}
            user={pipelineUser}
            stillOnboarding
            onOpen={setOnboardingDrawer}
          />
        </div>
      )}

      {/* The headline row: the fleet table's own six columns, per hotel. */}
      <div className="mt-4">
        <HotelMetricTiles
          metrics={hotelMetrics}
          recentDays={metrics.data?.recentDays ?? 14}
          currency={hotel.currency}
          unavailable={metrics.isError}
          isLoading={metrics.isLoading}
        />
      </div>

      {/* Go-live tripwire strip */}
      {hotel.goLive.goLiveAt && (
        <div
          className={`rounded-lg px-4 py-2.5 mb-4 text-sm ${
            hotel.goLive.result === "warning"
              ? "bg-danger-light text-danger-dark"
              : "bg-[#F4F4F6] text-content-secondary"
          }`}
        >
          Go-live {fmtDate(hotel.goLive.goLiveAt)} · 48-hour check{" "}
          {hotel.goLive.result ??
            `pending · due ${fmtDate(hotel.goLive.dueAt)}`}
          {hotel.goLive.result === "warning" && goLiveEvidence && (
            <> — {goLiveEvidence}</>
          )}
        </div>
      )}

      {/* THE operator flow: the problem → the fix → act, the whole incident
          thread flattened in place (there is no separate incident page). The
          hotel's timeline sits beside it, 70/30, matched in height — "when
          did this go red, and what has been tried" is the first question
          asked of an open incident. */}
      <div className="grid grid-cols-1 xl:grid-cols-10 gap-4 mb-4">
        <div className="xl:col-span-7 min-w-0">
          <IncidentPanel hotel={hotel} />
        </div>
        <div className="xl:col-span-3 min-w-0">
          <StateHistoryCard
            hotel={hotel}
            fixEvents={fixEvents}
            incidentEvents={incidentEvents}
            onRemoveRow={setRemovingRow}
          />
        </div>
      </div>

      {/* The two standing cards, side by side and matched in height: what the
          rules read, and how this hotel is set up and governed. Both are
          square-led rows, so the pair reads as one shape. */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-4">
        <HealthRulesCard
          rules={hotel.rules}
          conditionForRule={conditionForRule}
          onAcceptCondition={(rule) =>
            setModal({ kind: "accept-condition", rule })
          }
          className="h-full"
        />
        <SetupCard
          hotel={hotel}
          user={pipelineUser}
          onLock={(lock) =>
            setModal({
              kind: "lock",
              lock,
              action: hotel.locks[lock].locked ? "remove" : "apply",
            })
          }
          onLockHistory={() => setModal({ kind: "lock-history" })}
          onConditionAction={(condition, action) =>
            setModal({ kind: "condition", condition, action })
          }
          onOpenOnboarding={setOnboardingDrawer}
        />
      </div>

      {/* Booking routes takes the whole row unless there are support
          captures to sit beside it — the support tag ships only inside a
          container we control, so on a GA4-only hotel there is no second
          card and a half-width one would just be a gap. */}
      <div
        className={
          hotel.captures.mode === "gtm"
            ? "grid grid-cols-1 xl:grid-cols-2 gap-4"
            : ""
        }
      >
        <SectionCard
          title="Booking routes"
          className="h-full"
          bodyClassName="max-h-[340px] overflow-y-auto"
        >
          {hotel.routes.length > 0 ? (
            hotel.routes.map((route) => (
              <RouteChain key={route.routeId} route={route} />
            ))
          ) : hotel.routeDiscovery.attemptedAt === null ? (
            // Never walked — a gap in our records, not a verdict (§5.2).
            <p className="text-sm text-gray-500">
              no fingerprint yet — awaiting go-live walk
              {hotel.awaitingGoLiveRun && " (queued: backstop)"}
            </p>
          ) : (
            // Walked and found nothing — a verdict about the site.
            <p className="text-sm text-warning-dark">
              discovery walk ran {fmtDate(hotel.routeDiscovery.attemptedAt)} and
              found no booking route
            </p>
          )}
        </SectionCard>

        {hotel.captures.mode === "gtm" && (
          <SectionCard
            title="Support captures"
            className="h-full"
            bodyClassName="max-h-[340px] overflow-y-auto"
          >
            <CapturesCard hotel={hotel} />
          </SectionCard>
        )}
      </div>

      {/* Developer diagnostics — deliberately last on the page, collapsed,
          and the ONLY home of Diagnose now: a rules signal triggers its own
          Validator run on the daily pass, so hitting this by hand is a
          developer act, not the operator's flow. Collapsed it is one quiet
          bar; open it is the full six-stage pipeline. */}
      <div id="th-pipeline" className="mt-6 pb-2 scroll-mt-2">
        <button
          onClick={togglePipeline}
          className="w-full flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-dashed border-[#E9E9EB] text-left hover:bg-white/60 transition-colors group"
        >
          <span className="flex items-center gap-2 min-w-0">
            <span
              className={`inline-block text-[10px] text-gray-400 transition-transform ${
                pipelineOpen ? "rotate-90" : ""
              }`}
            >
              ▶
            </span>
            <span className="text-xs font-medium text-gray-500 group-hover:text-content-secondary">
              Developer diagnostics
            </span>
            <span className="text-xs text-gray-400 truncate">
              — the six stages a guest's click walks, with this hotel's last
              Validator run settled onto them
            </span>
          </span>
          <span className="text-xs text-gray-400 whitespace-nowrap shrink-0">
            {lastRunLabel}
          </span>
        </button>
        {pipelineOpen && (
          <div className="mt-2">
            <PipelineCard
              runId={freshRunId ?? hotel.lastRun?.id ?? null}
              pending={diagnose.isPending}
              animSignal={animSignal}
              onRunDiagnose={runDiagnose}
            />
          </div>
        )}
      </div>

      {/* Modals */}
      {modal?.kind === "lock" && (
        <LockActionModal
          customerId={customerId}
          lock={modal.lock}
          action={modal.action}
          pending={applyLock.isPending || removeLock.isPending}
          onClose={() => setModal(null)}
          onSubmit={(reason) => {
            const args = { lock: modal.lock, reason };
            const opts = { onSuccess: () => setModal(null) };
            if (modal.action === "apply") applyLock.mutate(args, opts);
            else removeLock.mutate(args, opts);
          }}
        />
      )}
      {modal?.kind === "override" && (
        <OverrideModal
          pending={applyOverride.isPending}
          onClose={() => setModal(null)}
          onSubmit={(body) =>
            applyOverride.mutate(body, { onSuccess: () => setModal(null) })
          }
        />
      )}
      {modal?.kind === "clear-override" && (
        <ClearOverridePrompt
          standing={hotel.status.override?.state ?? null}
          pending={clearOverride.isPending}
          onClose={() => setModal(null)}
          onSubmit={(reason) =>
            clearOverride.mutate(reason, { onSuccess: () => setModal(null) })
          }
        />
      )}
      {modal?.kind === "proof" && (
        <ProofRequirementModal
          current={hotel.proof.required}
          pending={setProof.isPending}
          onClose={() => setModal(null)}
          onSubmit={(body) =>
            setProof.mutate(body, { onSuccess: () => setModal(null) })
          }
        />
      )}
      {modal?.kind === "access" && (
        <AccessModeModal
          access={hotel.access}
          pending={setAccessMode.isPending}
          onClose={() => setModal(null)}
          onSubmit={(body) =>
            setAccessMode.mutate(body, { onSuccess: () => setModal(null) })
          }
        />
      )}
      {modal?.kind === "lock-history" && (
        <LockHistoryModal
          history={hotel.locks.history}
          onClose={() => setModal(null)}
          onRemoveRow={(event) =>
            setRemovingRow({
              log: "lock-event",
              rowId: event.id,
              label: `${event.lock} lock ${
                event.action === "apply" ? "applied" : "removed"
              }`,
              detail: event.reason,
            })
          }
        />
      )}
      {removingRow && (
        <RemoveHistoryRowModal
          log={removingRow.log}
          label={removingRow.label}
          detail={removingRow.detail}
          pending={removeHistoryRow.isPending}
          onClose={() => setRemovingRow(null)}
          onSubmit={(reason) =>
            removeHistoryRow.mutate(
              {
                log: removingRow.log,
                rowId: removingRow.rowId,
                reason,
              },
              { onSuccess: () => setRemovingRow(null) },
            )
          }
        />
      )}
      {modal?.kind === "accept-condition" && (
        <AcceptConditionModal
          scopeType="rule"
          scopeId={modal.rule.rule}
          scopeLabel={`Rule ${modal.rule.rule} — ${modal.rule.title}`}
          pending={addCondition.isPending}
          onClose={() => setModal(null)}
          onSubmit={(body) =>
            addCondition.mutate(body, { onSuccess: () => setModal(null) })
          }
        />
      )}
      {modal?.kind === "condition" && (
        <ConditionActionModal
          condition={modal.condition}
          action={modal.action}
          pending={removeCondition.isPending || confirmCondition.isPending}
          onClose={() => setModal(null)}
          onSubmit={(text) => {
            const opts = { onSuccess: () => setModal(null) };
            if (modal.action === "remove") {
              removeCondition.mutate(
                { conditionId: modal.condition.id, reason: text },
                opts,
              );
            } else {
              confirmCondition.mutate(
                { conditionId: modal.condition.id, note: text || undefined },
                opts,
              );
            }
          }}
        />
      )}

      {/* Onboarding drawers — the full pipeline panel when one exists,
          the bare brand-assets panel otherwise. */}
      {onboardingDrawer &&
        (pipelineUser ? (
          <UserDetailPanel
            user={pipelineUser}
            initialTab={onboardingDrawer}
            onClose={() => setOnboardingDrawer(null)}
          />
        ) : hotel.userId ? (
          <BrandAssetsDrawer
            userId={hotel.userId}
            hotelName={hotel.hotelName}
            onClose={() => setOnboardingDrawer(null)}
          />
        ) : null)}
    </Shell>
  );
}

// ─── Pieces ───────────────────────────────────────────────────────────────────

/**
 * Where a context note came from, for the reader who wonders — on hover only.
 *
 * Nothing generates these. They are hand-written census entries (the eight
 * `SPECIAL_CONTEXT` paragraphs), keyed by Ads customer id and loaded ONCE by
 * the rollout seeding pass, which is why the actor reads `system:seed`; an
 * admin-written one would carry that admin's address instead. No model, no
 * agent, no rule reads or writes them.
 */
function contextProvenance(hotel: HotelDetailData): string {
  const who =
    hotel.contextNoteSetBy === "system:seed"
      ? "Loaded by the rollout seeding pass from the hand-written fleet census"
      : hotel.contextNoteSetBy
        ? `Written by ${fmtActor(hotel.contextNoteSetBy)}`
        : "Standing hand-written context";
  const when = hotel.contextNoteSetAt
    ? ` on ${fmtDate(hotel.contextNoteSetAt)}`
    : "";
  return `${who}${when}. It describes what was learned about this hotel, whenever that was — nothing computes from it, and nothing keeps it current.`;
}

/**
 * The derived Low-volume reading as one line under the header (ADR 0039).
 *
 * The numbers are the sentence: the two windows' counts against the threshold
 * they were judged under, and since when the reading has stood. The second
 * sentence is the consequence, stated both ways — which rules stand down AND
 * which keep watching — because "low volume" on its own invites the reading
 * that the hotel has been filed away, and a Healthy hotel whose Rule 2, 3 or
 * 6 fires is still marked Unhealthy the same morning it would have been.
 *
 * The windows' exact dates ride the hover rather than the line: an operator
 * acts on the counts, and the dates are for whoever wants to check them
 * against the fleet table's own columns.
 */
function LowVolumeStrip({ facts }: { facts: LowVolumeFacts }) {
  const windows =
    `Trailing window ${fmtDate(facts.periodFrom)} – ${fmtDate(facts.periodTo)} · ` +
    `prior window ${fmtDate(facts.priorFrom)} – ${fmtDate(facts.priorTo)} · ` +
    `booking data through ${fmtDate(facts.dataThroughDay)}. Re-read at every ` +
    `daily pass; the threshold and the window are Tracking Health settings.`;
  return (
    <div
      className="bg-[#F4F4F6] rounded-lg px-4 py-3 mb-4 mt-2 text-sm"
      data-tip={windows}
    >
      <TileLabel>Low volume</TileLabel>
      <p className="text-content-primary">
        Low volume — {facts.bookings} and {facts.priorBookings} qualifying
        bookings in the last two {facts.windowDays}-day windows (fewer than{" "}
        {facts.threshold})
        {facts.since && <> since {fmtDate(facts.since)}</>}. Rules 1, 4 and 5
        stand down; the click floor and Rules 2–3 still watch it.
      </p>
    </div>
  );
}

const CARD_BUTTON_CLASS =
  "px-3 py-1.5 text-sm font-medium rounded-md border border-brand-primary text-brand-primary hover:bg-[#E7F1F3]";

/**
 * The onboarding face of a hotel: its provisioning pipeline (when one
 * exists) and the brand-assets surface, both opening as drawers. Leads the
 * page while the hotel is gated not-yet-live; sits with the other cards once
 * it is live. Renders nothing when there is neither a pipeline nor a
 * platform user to key brand assets on.
 */
function OnboardingCard({
  hotel,
  user,
  stillOnboarding,
  onOpen,
}: {
  hotel: HotelDetailData;
  user: ProvisioningUser | null;
  stillOnboarding: boolean;
  onOpen: (tab: "pipeline" | "brand-assets") => void;
}) {
  if (!user && !hotel.userId) return null;

  return (
    <SectionCard
      title={stillOnboarding ? "Still onboarding" : "Onboarding & brand assets"}
      headerRight={
        user ? (
          <span className="flex items-center gap-1.5">
            <ModeBadge setupMode={user.setupMode} />
            {user.isGa4Only && <Chip tone="purple">GA4</Chip>}
          </span>
        ) : undefined
      }
    >
      {user ? (
        <div className="flex flex-col gap-3">
          <PipelineStepSummary
            user={user}
            awaitingFirstSpend={stillOnboarding}
          />
          <div className="text-xs text-content-secondary">
            started {fmtDate(user.startedAt)} · updated{" "}
            {fmtDate(user.lastUpdatedAt)}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              className={CARD_BUTTON_CLASS}
              onClick={() => onOpen("pipeline")}
            >
              View pipeline
            </button>
            <button
              className={CARD_BUTTON_CLASS}
              onClick={() => onOpen("brand-assets")}
            >
              Brand assets
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-gray-500">
            No provisioning pipeline recorded — this hotel onboarded before the
            pipeline shipped.
          </p>
          <div>
            <button
              className={CARD_BUTTON_CLASS}
              onClick={() => onOpen("brand-assets")}
            >
              Brand assets
            </button>
          </div>
        </div>
      )}
    </SectionCard>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <Page>
      <div className="p-4 md:h-[calc(100dvh-62px)] overflow-auto lg:h-[100dvh] bg-neutral-200 flex-1">
        {children}
      </div>
      <TooltipLayer />
    </Page>
  );
}

/**
 * How this hotel is set up and governed, in ONE card: the two locks, the
 * accepted conditions standing over them, and the provisioning pipeline that
 * built it. Deliberately the same shape as the health-rules card it sits
 * beside — a glyph square, a title, a right-hand chip, a detail line — so the
 * two read as one row of the page rather than two unrelated boxes.
 *
 * The lock squares ACT: clicking one applies or removes that lock, the same
 * click the fleet row's R / V glyphs carry.
 *
 * So do the conditions: each carries `remove…`, and a flagged one carries
 * `re-confirm` — the only way a review flag ever clears, since conditions are
 * never auto-retired. Both actions were reachable from the API alone until
 * now, which left the two automated flags (a Problem 2 route change, a
 * re-enrollment cycle) raising a mark the portal could render and nobody
 * could clear.
 */
function SetupCard({
  hotel,
  user,
  onLock,
  onLockHistory,
  onConditionAction,
  onOpenOnboarding,
}: {
  hotel: HotelDetailData;
  user: ProvisioningUser | null;
  onLock: (lock: TrackingLockKind) => void;
  onLockHistory: () => void;
  onConditionAction: (
    condition: ConditionRow,
    action: "remove" | "confirm",
  ) => void;
  onOpenOnboarding: (tab: "pipeline" | "brand-assets") => void;
}) {
  const conditions = hotel.conditions.filter((c) => c.status === "active");
  return (
    <SectionCard
      title="Locks, conditions & onboarding"
      className="h-full"
      headerRight={
        <button
          onClick={onLockHistory}
          className="text-xs font-medium text-brand-primary hover:text-brand-primary-hover"
        >
          lock history
        </button>
      }
    >
      <div className="-mx-2">
        <LockRow lock="rules" facts={hotel.locks.rules} onAct={onLock} />
        <LockRow
          lock="validator"
          facts={hotel.locks.validator}
          onAct={onLock}
        />

        {conditions.length === 0 ? (
          <SetupRow
            glyph="—"
            tone="none"
            title="Accepted conditions"
            right={<span className="text-xs text-gray-400">none</span>}
          >
            <span className="text-xs text-gray-500">
              Nothing on this hotel is being deliberately tolerated.
            </span>
          </SetupRow>
        ) : (
          conditions.map((condition) => (
            <SetupRow
              key={condition.id}
              // `R3` matches the rules card's own squares, so a rule-scoped
              // condition is recognisable at a glance. A problem-scoped one
              // gets a plain mark and says which problem in words — `P3` is
              // how the catalog indexes a fix strategy, not something an
              // operator should have to decode.
              glyph={
                condition.scopeType === "rule" ? `R${condition.scopeId}` : "✓"
              }
              tone="condition"
              glyphTitle={
                condition.scopeType === "rule"
                  ? `Accepted condition scoped to health Rule ${condition.scopeId}`
                  : `Accepted condition scoped to Problem ${condition.scopeId}`
              }
              title={
                condition.scopeType === "rule"
                  ? `Accepted — Rule ${condition.scopeId}`
                  : `Accepted — ${
                      PROBLEM_SHORT_LABELS[condition.scopeId as ProblemId] ??
                      `Problem ${condition.scopeId}`
                    }`
              }
              right={
                <span className="flex items-center gap-2">
                  {/* No category chip. Accepting is one decision — on or off
                      — and `commercial` / `structural` is a taxonomy the
                      store keeps, not something an operator reads a row for.
                      What actually differs between the two is whether there
                      is an exit condition, and that renders below in words. */}
                  <span className="text-xs text-gray-400">
                    {condition.ageDays}d
                  </span>
                  {condition.reviewFlag && (
                    <Chip
                      tone="orange"
                      title={
                        CONDITION_REVIEW_FLAG_EXPLANATION[condition.reviewFlag]
                      }
                    >
                      re-review
                    </Chip>
                  )}
                </span>
              }
            >
              <span className="text-xs text-gray-500">
                {condition.reason}
                {condition.exitCondition && (
                  <span className="block text-gray-400">
                    look again if: {condition.exitCondition}
                  </span>
                )}
              </span>
              {/* The two human exits. `re-confirm` appears only under a flag
                  — it clears the flag and nothing else. */}
              <span className="flex items-center gap-3 mt-1">
                {condition.reviewFlag && (
                  <button
                    onClick={() => onConditionAction(condition, "confirm")}
                    data-tip={CONDITION_CONFIRM_DISCLOSURE}
                    className="text-xs font-medium text-brand-primary hover:text-brand-primary-hover"
                  >
                    re-confirm
                  </button>
                )}
                <button
                  onClick={() => onConditionAction(condition, "remove")}
                  data-tip={CONDITION_REMOVE_DISCLOSURE}
                  className="text-xs font-medium text-gray-500 hover:text-content-primary"
                >
                  remove…
                </button>
              </span>
            </SetupRow>
          ))
        )}

        {(user || hotel.userId) && (
          <SetupRow
            glyph="⚙"
            tone="onboarding"
            glyphTitle={
              user
                ? "The SEM provisioning pipeline that built this hotel's tracking — click to open it"
                : "No pipeline on record — click to open this hotel's brand assets"
            }
            title="Onboarding & brand assets"
            onRowClick={() =>
              onOpenOnboarding(user ? "pipeline" : "brand-assets")
            }
            actionHint={user ? "open pipeline →" : "brand assets →"}
            // No `Legacy` / `GA4` tags here. The setup mode is a fact about
            // how this hotel was onboarded — it belongs to the pipeline drawer
            // this row opens, and the GA4-only fact is already a chip beside
            // the hotel's name at the top of the page.
          >
            {/* No buttons on this row. The row IS the button — the drawer it
                opens carries both tabs, so a `Brand assets` button beside a
                `View pipeline` button beside a clickable row was three ways
                into one drawer, two of which had to stop the third's click to
                work at all. */}
            {user ? (
              <div className="flex flex-col gap-2">
                {/* A finished pipeline says nothing worth a line: every live
                    hotel's is finished. Only an unfinished one earns copy. */}
                {user.overallStatus !== "COMPLETED" && (
                  <PipelineStepSummary user={user} awaitingFirstSpend={false} />
                )}
                <span className="text-xs text-gray-400">
                  started {fmtDate(user.startedAt)} · updated{" "}
                  {fmtDate(user.lastUpdatedAt)} · brand assets are a tab inside
                </span>
              </div>
            ) : (
              <span className="text-xs text-gray-500">
                No provisioning pipeline recorded — this hotel onboarded before
                the pipeline shipped. The row opens its brand assets.
              </span>
            )}
          </SetupRow>
        )}
      </div>
    </SectionCard>
  );
}

type SetupTone = "locked" | "unlocked" | "condition" | "onboarding" | "none";

const SETUP_TONE_CLASS: Record<SetupTone, string> = {
  locked: "bg-warning-light text-warning-dark",
  unlocked: "border border-dashed border-gray-300 text-gray-400",
  // ONE tone for every accepted condition. It used to split by reason
  // category, which coloured two rows differently for a distinction the UI
  // no longer names anywhere — a difference the reader could see and not
  // account for.
  condition: "bg-info-light text-info-dark",
  // Dashed, like an unplaced lock and like `none`: the filled grey square read
  // as a state (something IS set here) when the row is only a way in. Filled
  // squares on this card mean a standing disposition — a lock placed, a
  // condition accepted — and nothing else should wear one.
  onboarding: "border border-dashed border-gray-300 text-gray-400",
  none: "border border-dashed border-gray-300 text-gray-300",
};

/**
 * The health-rules row shape, with a glyph square instead of a rule number.
 * `onRowClick` makes the WHOLE row the action — the fleet table's habit, and
 * the reason the glyph is not a button of its own: an operator should not
 * have to find the 28px square to place a lock.
 */
function SetupRow({
  glyph,
  tone,
  glyphTitle,
  title,
  titleHint,
  right,
  onRowClick,
  actionHint,
  children,
}: {
  glyph: string;
  tone: SetupTone;
  glyphTitle?: string;
  title: string;
  titleHint?: string;
  right?: React.ReactNode;
  onRowClick?: () => void;
  /** What clicking the row does, shown on hover at the row's right edge. */
  actionHint?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      onClick={onRowClick}
      data-tip={onRowClick ? glyphTitle : undefined}
      className={`flex items-start gap-3 px-2 py-2.5 rounded-md border-b border-[#E9E9EB] last:border-b-0 transition-colors group/row ${
        onRowClick ? "cursor-pointer hover:bg-[#F4F4F6]" : "hover:bg-[#FAFAFB]"
      }`}
    >
      <span className="pt-0.5">
        <span
          data-tip={glyphTitle}
          className={`inline-flex items-center justify-center h-7 min-w-[28px] px-1 rounded-md text-[13px] font-semibold shrink-0 ${SETUP_TONE_CLASS[tone]}`}
        >
          {glyph}
        </span>
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <span
            className="text-sm font-medium text-content-primary"
            data-tip={titleHint}
          >
            {title}
          </span>
          {/* The hover hint sits BEFORE the standing fact, not after it. The
              fact (`none`, `🔒 applied`, an age) is what the eye scans down
              this card's right edge, and a hint appearing outside it pushed
              every row's fact left the moment a pointer crossed it — so the
              column only lined up when nothing was hovered. */}
          <span className="flex items-center gap-2 shrink-0">
            {actionHint && (
              <span className="text-xs font-medium text-brand-primary opacity-0 group-hover/row:opacity-100 transition-opacity">
                {actionHint}
              </span>
            )}
            {right}
          </span>
        </div>
        <div className="mt-1">{children}</div>
      </div>
    </div>
  );
}

function LockRow({
  lock,
  facts,
  onAct,
}: {
  lock: TrackingLockKind;
  facts: LockFacts;
  onAct: (lock: TrackingLockKind) => void;
}) {
  const label = lock === "rules" ? "Rules lock" : "Validator lock";
  return (
    <SetupRow
      glyph={lock === "rules" ? "R" : "V"}
      tone={facts.locked ? "locked" : "unlocked"}
      glyphTitle={`${LOCK_EXPLANATIONS[lock]}\nClick this row to ${facts.locked ? "remove" : "apply"} it.`}
      title={label}
      titleHint={LOCK_EXPLANATIONS[lock]}
      onRowClick={() => onAct(lock)}
      actionHint={facts.locked ? "remove →" : "apply →"}
      right={
        facts.locked ? (
          <Chip tone="orange" title={LOCK_EXPLANATIONS[lock]}>
            🔒 {facts.auto ? "auto-applied" : "applied"}
          </Chip>
        ) : (
          <span className="text-xs text-gray-400">none</span>
        )
      }
    >
      <span className="text-xs text-gray-500">
        {facts.locked ? (
          <>
            applied {fmtDate(facts.appliedAt)} ·{" "}
            {facts.auto
              ? "auto-applied"
              : `by ${fmtActor(facts.appliedBy) || "?"}`}
            {facts.reason && <> · {facts.reason}</>}
          </>
        ) : (
          LOCK_SHORT_HINTS[lock]
        )}
      </span>
    </SetupRow>
  );
}

/**
 * The hotel's history: ONE chronological list, newest first.
 *
 * It was three lists until 2026-08-14 — state changes, then a "Fixes & past
 * incidents" block underneath, each with its own row styling. The split was by
 * where the data came from (the state table, the incident thread, the incident
 * history) rather than by anything a reader cares about, and it meant a fix
 * logged this morning rendered BELOW a state change from March. Sorting can
 * only be done once, over everything, or it is not sorting.
 *
 * So every row now has the same shape — date, a chip saying what kind of thing
 * happened, the detail, the actor — and differs only in what it says. The
 * chip's tone still separates the KINDS, which is the part that had to survive
 * the merge: a state change is derived by the machine, a disposition is a human
 * decision that deliberately changed no state, and the two must never be read
 * as the same act.
 */
function StateHistoryCard({
  hotel,
  fixEvents,
  incidentEvents,
  onRemoveRow,
}: {
  hotel: HotelDetailData;
  fixEvents: IncidentEventRow[];
  incidentEvents: IncidentEventRow[];
  onRemoveRow: (row: RemovableRow) => void;
}) {
  const timeline = buildTimeline(hotel, incidentEvents, fixEvents, onRemoveRow);
  return (
    <SectionCard
      title="History"
      className="h-full"
      bodyClassName="max-h-[62vh] overflow-y-auto"
    >
      {timeline.length === 0 ? (
        <p className="text-sm text-gray-500">
          Nothing recorded yet — the first recording is the seeding pass.
        </p>
      ) : (
        timeline.map((entry) => <div key={entry.key}>{entry.node}</div>)
      )}
    </SectionCard>
  );
}

interface TimelineEntry {
  key: string;
  at: string;
  node: React.ReactNode;
}

/** One history row, named as the reader sees it, ready for the removal modal. */
interface RemovableRow {
  log: HistoryLog;
  rowId: string;
  label: string;
  detail: string | null;
}

/**
 * Everything that ever happened to this hotel, newest first, in one list.
 *
 * Four sources — derived state changes, silences off the incident thread,
 * accepted conditions, fix reports, and closed incidents — merged and sorted by
 * timestamp ALONE. Ties keep insertion order, which puts the state change above
 * the disposition taken about it.
 *
 * Two limits are inherited rather than introduced, and worth knowing: only the
 * OPEN incident's thread is fetched, so a silence or a fix logged on a closed
 * incident is not here; and a condition contributes its apply and its removal
 * as two rows, because they happened at two times.
 */
function buildTimeline(
  hotel: HotelDetailData,
  incidentEvents: IncidentEventRow[],
  fixEvents: IncidentEventRow[],
  onRemove: (row: RemovableRow) => void,
): TimelineEntry[] {
  const entries: TimelineEntry[] = hotel.stateHistory.map((row) => {
    const label = row.fromState
      ? `${row.fromState} → ${row.toState}`
      : `First recorded: ${row.toState}`;
    return {
      key: `state-${row.id}`,
      at: row.at,
      node: (
        <HistoryRow
          at={row.at}
          chip={
            row.fromState === null ? (
              // A hotel's FIRST recorded state — never `null → X` (§5.2).
              <span className="flex items-center gap-1.5">
                <span className="text-xs text-content-secondary">
                  first recorded:
                </span>
                <StateChipBare state={row.toState} />
              </span>
            ) : (
              <span className="flex items-center gap-1.5">
                <StateChipBare state={row.fromState} />
                <span className="text-gray-400">→</span>
                <StateChipBare state={row.toState} />
              </span>
            )
          }
          suffix={
            row.trigger === "seed-derivation"
              ? "seeding pass"
              : row.trigger === "admin-override"
                ? "admin-override"
                : null
          }
          detail={row.reason}
          actor={null}
          onRemove={() =>
            onRemove({
              log: "state-change",
              rowId: row.id,
              label,
              detail: row.reason,
            })
          }
        />
      ),
    };
  });

  incidentEvents.forEach((event) => {
    if (event.type !== "silenced" && event.type !== "silence-cleared") return;
    const label = event.type === "silenced" ? "🔕 Silenced" : "Silence ended";
    entries.push({
      key: `silence-${event.id}`,
      at: event.at,
      node: (
        <HistoryRow
          at={event.at}
          chip={<Chip tone="orange">{label}</Chip>}
          detail={event.reason}
          actor={event.actor}
          // Removing a `silenced` row while its silence still stands is
          // refused server-side, pointing at silence/clear — an
          // active-but-invisible silence is the worst outcome available.
          onRemove={() =>
            onRemove({
              log: "incident-event",
              rowId: event.id,
              label,
              detail: event.reason,
            })
          }
        />
      ),
    });
  });

  fixEvents.forEach((event) => {
    const problemId = fixProblemId(event);
    const problem = problemId
      ? (PROBLEM_SHORT_LABELS[problemId as ProblemId] ??
        `Problem ${problemId}`)
      : null;
    entries.push({
      key: `fix-${event.id}`,
      at: event.at,
      node: (
        <HistoryRow
          at={event.at}
          // Which problem the fix was logged against. Without it every entry
          // reads `fix logged` over a free-text note, and on a plan carrying
          // several problems there is no way to tell which one was reported.
          chip={
            <Chip tone="teal">
              {problem ? `Fix logged — ${problem}` : "Fix logged"}
            </Chip>
          }
          suffix={event.editedAt ? "edited" : null}
          detail={event.reason}
          actor={event.actor}
          onRemove={() =>
            onRemove({
              log: "incident-event",
              rowId: event.id,
              label: problem ? `Fix logged — ${problem}` : "Fix logged",
              detail: event.reason,
            })
          }
        />
      ),
    });
  });

  hotel.conditions.forEach((condition) => {
    entries.push({
      key: `condition-${condition.id}`,
      at: condition.appliedAt,
      node: (
        <HistoryRow
          at={condition.appliedAt}
          chip={
            <Chip tone="blue">{`Condition accepted — ${
              condition.scopeType === "rule"
                ? `Rule ${condition.scopeId}`
                : (PROBLEM_SHORT_LABELS[condition.scopeId as ProblemId] ??
                  `Problem ${condition.scopeId}`)
            }`}</Chip>
          }
          detail={condition.reason}
          actor={condition.appliedBy}
        />
      ),
    });
    if (condition.removedAt) {
      entries.push({
        key: `condition-removed-${condition.id}`,
        at: condition.removedAt,
        node: (
          <HistoryRow
            at={condition.removedAt}
            chip={<Chip tone="neutral">Condition removed</Chip>}
            detail={condition.removeReason ?? condition.reason}
            actor={condition.removedBy}
          />
        ),
      });
    }
  });

  hotel.incidentHistory
    .filter((row) => row.closedAt)
    .forEach((row) => {
      entries.push({
        key: `incident-${row.id}`,
        at: row.closedAt as string,
        node: (
          <HistoryRow
            at={row.closedAt as string}
            chip={
              <Chip tone="neutral">
                {row.status === "verified"
                  ? "Incident closed — verified"
                  : "Incident closed — left fleet"}
              </Chip>
            }
            suffix={`open since ${fmtDate(row.openedAt)}`}
            detail={row.primaryIssue ?? `#${row.id.slice(0, 8)}`}
            actor={null}
          />
        ),
      });
    });

  return entries.sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
}

/** A `fix-logged` event names its problem in evidence; nothing else does. */
function fixProblemId(event: IncidentEventRow): string | null {
  const problem = event.evidence?.problem;
  return typeof problem === "string" ? problem : null;
}

/**
 * THE history row. Every entry in the timeline is this shape — date, a chip
 * saying what happened, an optional qualifier, the detail, the actor — so the
 * list reads as one story rather than as three tables stacked on each other.
 *
 * The chip's TONE carries the distinction the merge had to preserve: a state
 * change is derived by the machine and wears its state chips; a human decision
 * that deliberately changed no state (a silence, an accepted condition) wears a
 * coloured chip naming itself as such. Nothing in the row's own styling implies
 * which kind it is, which is the point — the words do.
 */
function HistoryRow({
  at,
  chip,
  suffix,
  detail,
  actor,
  onRemove,
}: {
  at: string;
  chip: React.ReactNode;
  suffix?: string | null;
  detail: string | null;
  actor: string | null;
  onRemove?: () => void;
}) {
  return (
    <div className="group py-2 border-b border-[#E9E9EB] last:border-b-0 text-sm">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-gray-500">{fmtDate(at)}</span>
        {chip}
        {suffix && <span className="text-xs text-gray-400">· {suffix}</span>}
        {onRemove && <RemoveRowButton onClick={onRemove} className="ml-auto" />}
      </div>
      {(detail || actor) && (
        <div className="text-xs text-gray-500 mt-0.5">
          {detail}
          {actor && (
            <span className="text-gray-400"> · {fmtActor(actor)}</span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The one affordance every removable history row wears — held back until the
 * row is hovered, because a history is for reading and a page of visible
 * delete buttons invites tidying rather than recording.
 *
 * "Remove" is honest about its scope in the tooltip: it hides the row from
 * these pages, and every machine derivation goes on seeing it, so nothing the
 * row records is undone.
 */
function RemoveRowButton({
  onClick,
  className = "",
}: {
  onClick: () => void;
  className?: string;
}) {
  return (
    <button
      onClick={onClick}
      data-tip="Hide this row from the history — it does not undo what the row records"
      className={`px-1.5 py-0.5 text-[11px] font-medium rounded text-gray-400 hover:text-danger-dark hover:bg-danger-light/50 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity shrink-0 ${className}`}
    >
      Remove
    </button>
  );
}

/** GTM-tracked hotels only — the caller gates on `captures.mode`, since a
 *  hotel with no container of ours can carry no support tag to capture with. */
function CapturesCard({ hotel }: { hotel: HotelDetailData }) {
  const captures = hotel.captures;
  // Backend issue 42: the latest run OBSERVES the tag in the served payload,
  // so the deployment claim §11.4 withdrew is back — stated only when a run
  // actually observed, never inferred from the capture store.
  const supportTag = captures.supportTag;
  const deploymentLine = supportTag ? (
    supportTag.present && supportTag.enabled ? (
      <p className="text-xs text-gray-400 mt-0.5">
        support tag deployed — observed in the served container{" "}
        {fmtDate(supportTag.observedAt)}
      </p>
    ) : supportTag.present ? (
      <p className="text-xs text-warning-dark mt-0.5">
        support tag deployed but PAUSED — it captures nothing until re-enabled
        (observed {fmtDate(supportTag.observedAt)})
      </p>
    ) : (
      <p className="text-xs text-warning-dark mt-0.5">
        support tag not deployed to this container (served payload observed{" "}
        {fmtDate(supportTag.observedAt)})
      </p>
    )
  ) : null;

  if (captures.total30d === 0) {
    // The store witnesses containers that FIRED — zero captures alone is
    // evidence about bookings; the deployment line above is what separates
    // "no bookings" from "no tag", and only an observation may say so.
    return (
      <div>
        <p className="text-sm text-content-secondary">
          No captures in the last 30 days
        </p>
        <p className="text-xs text-gray-400 mt-0.5">
          a capture is written only when a real guest completes a purchase
        </p>
        {deploymentLine}
      </div>
    );
  }
  return (
    <div>
      <VerdictCounters counts={captures.counts30d} />
      {captures.lastCaptureAt && (
        <p className="text-xs text-gray-500 mt-2">
          last capture {fmtDate(captures.lastCaptureAt)}
        </p>
      )}
      {deploymentLine}
    </div>
  );
}

/**
 * Clear-override is a one-field prompt used only here. It takes the STANDING
 * override because clearing Low volume is not the same act as clearing a
 * label: it lifts the two locks the category placed, and the hotel's dark
 * period under them resets the trusted data start date. An operator owed that
 * sentence before pressing the button, not after.
 */
function ClearOverridePrompt({
  standing,
  onSubmit,
  onClose,
  pending,
}: {
  standing: OverrideState | null;
  onSubmit: (reason: string) => void;
  onClose: () => void;
  pending?: boolean;
}) {
  const [reason, setReason] = useState("");
  const lowVolume = standing === "low-volume";
  return (
    <ActionModal
      title={lowVolume ? "Remove from Low volume" : "Clear status override"}
      submitLabel={lowVolume ? "Remove from Low volume" : "Clear override"}
      onSubmit={() => onSubmit(reason.trim())}
      onClose={onClose}
      pending={pending}
      disabled={!reason.trim()}
    >
      <div>
        <label className={FIELD_LABEL_CLASS}>Reason (required)</label>
        <textarea
          className={`${INPUT_CLASS} min-h-[66px]`}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          autoFocus
        />
      </div>
      {lowVolume && (
        <p className="text-xs text-warning-dark rounded bg-warning-light/50 px-3 py-2.5">
          {LOW_VOLUME_EXIT_DISCLOSURE}
        </p>
      )}
      <p className="text-xs text-gray-400">
        The reported status returns to the computed state.
      </p>
    </ActionModal>
  );
}
